import React, { useState, useEffect } from 'react';
import {

  TextField,
  Box,
  IconButton
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Helmet } from 'react-helmet-async';
import { blue, red } from '@mui/material/colors';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

import { deleteEntry, get } from 'src/services/httpClient.service';
import { authenticationService } from 'src/services/authentication.service';

import { useNavigate } from 'react-router';
import ConfirmDeleteDialog from 'src/components/DeleteDialogbox/ConfirmDeleteDialog';



const CompanyScreen = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const APIURL = process.env.REACT_APP_BASE_API_URL;

  const {vendorId, franchiseId} = authenticationService.getVendorFranchiseIds();
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteRowData, setDeleteRowData] = useState<any>(null);
 

  // ✅ Fetch company list
  const fetchCompanies = async () => {
    try {
      // const response = await axios.get(`${APIURL}/api/companies/findAll`);
      const response = await get(`${APIURL}/api/company/vendor/${vendorId}/fetchAll`);
      const data = Array.isArray(response) ? response : response.data || [];
      setTableData(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      showSnackbar('Failed to fetch companies', 'error');
      setTableData([]);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // ✅ Handle Delete
  const handleDeleteClick = (rowData: any) => {
    setDeleteRowData(rowData);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteRowData) {
      try {
        const response = await deleteEntry(`${APIURL}/api/company/${deleteRowData.id}?vendorId=${vendorId}`);
        console.log('response',response)
        
          showSnackbar('Company deleted successfully!', 'success');
          await fetchCompanies();
        
      } catch (error) {
        showSnackbar('Error deleting company.', 'error');
      }
    } 
    setDeleteConfirmationOpen(false);
    setDeleteRowData(null);
  };

  // ✅ Handle Edit
  const handleEdit = async (id: string) => {

    navigate(`/company/Updatecompany/${id}`)

  };

  // ✅ Search Filter
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredRows = tableData.filter((row) =>
    Object.values(row).some(
      (value) => value && value.toString().toLowerCase().includes(searchQuery)
    )
  );

  // ✅ DataGrid Columns
  const columns = [
    { field: 'companyName', headerName: 'Company Name', flex: 2, minWidth: 200 },
    { field: 'email', headerName: 'Email', flex: 2, minWidth: 220 },
    { field: 'mobileNo', headerName: 'Mobile No', flex: 1.5, minWidth: 180 },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1.5,
      minWidth: 180,
      valueGetter: (params: any) =>
        params.value ? dayjs(params.value).format('DD/MM/YYYY') : ''
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <span
          style={{
            color: params.value === 'Approved' ? 'green' : 'gray',
            fontWeight: 500,
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params: any) => (
        <>
          <IconButton onClick={() => handleEdit(params.row.id)} sx={{ color: blue[500] }}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: red[500] }}>
            <DeleteIcon />
          </IconButton>
        </>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Manage Company</title>
      </Helmet>

      <PageTitleWrapper>
        <PageTitle
          heading="Manage Company"
          subHeading="Manage companies and their details" 
          handleClick={() => navigate('/Company/AddCompany')}
          label="Add"       />
      </PageTitleWrapper>
 

      {/* 🔍 Search */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '25%', padding: '0 5px' }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
          fullWidth
          sx={{ marginLeft: '16px' }}
        />
      </Box>

      {/* 📋 DataGrid */}
      <Box sx={{ height: 'calc(100vh - 250px)', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          components={{ Toolbar: GridToolbar }}
          pagination
          pageSize={paginationModel.pageSize}
          onPageSizeChange={(newPageSize) =>
            setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize }))
          }
          page={paginationModel.page}
          onPageChange={(newPage) =>
            setPaginationModel((prev) => ({ ...prev, page: newPage }))
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
          disableSelectionOnClick
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell:hover': { color: 'rgb(43 44 45)' },
            '& .MuiDataGrid-footerContainer': {  color: '#fff' },
          }}
        />
      </Box>

      {/* 🗑️ Delete Confirmation */}
      

      <ConfirmDeleteDialog
  open={deleteConfirmationOpen}
  onClose={() => setDeleteConfirmationOpen(false)}
  onConfirm={handleDeleteConfirm}
  itemName={deleteRowData?.companyName}
  context="Are you sure you want to delete company"
/>


    </>
  );
};

export default CompanyScreen;